import React from 'react';
import { useAuth } from '@clerk/clerk-react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import './App.css';
import AppWorkspacePage from './pages/AppWorkspacePage';
import AuthPage from './pages/AuthPage';
import LandingPage from './pages/LandingPage';

const clerkEnabled = Boolean(process.env.REACT_APP_CLERK_PUBLISHABLE_KEY);

const LoadingScreen: React.FC = () => {
  return (
    <div className="route-loading" role="status" aria-live="polite">
      <div className="route-loading-dot" />
      <p>Loading workspace…</p>
    </div>
  );
};

const ProtectedAppRouteWithClerk: React.FC = () => {
  const { isLoaded, isSignedIn } = useAuth();
  const location = useLocation();

  if (!isLoaded) {
    return <LoadingScreen />;
  }

  if (!isSignedIn) {
    const destination = `${location.pathname}${location.search}`;
    return <Navigate to="/login" replace state={{ from: destination }} />;
  }

  return <AppWorkspacePage />;
};

const PublicAuthRouteWithClerk: React.FC<{ mode: 'login' | 'signup' }> = ({ mode }) => {
  const { isLoaded, isSignedIn } = useAuth();

  if (!isLoaded) {
    return <LoadingScreen />;
  }

  if (isSignedIn) {
    return <Navigate to="/app" replace />;
  }

  return <AuthPage mode={mode} clerkEnabled={true} />;
};

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <div className="app-shell">
        <Routes>
          <Route path="/" element={<LandingPage clerkEnabled={clerkEnabled} />} />

          <Route
            path="/login"
            element={
              clerkEnabled ? (
                <PublicAuthRouteWithClerk mode="login" />
              ) : (
                <AuthPage mode="login" clerkEnabled={false} />
              )
            }
          />

          <Route
            path="/signup"
            element={
              clerkEnabled ? (
                <PublicAuthRouteWithClerk mode="signup" />
              ) : (
                <AuthPage mode="signup" clerkEnabled={false} />
              )
            }
          />

          <Route
            path="/app"
            element={
              clerkEnabled ? <ProtectedAppRouteWithClerk /> : <AppWorkspacePage />
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </BrowserRouter>
  );
};

export default App;
